import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAppSettings } from "@/contexts/AppSettingsContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Save, GripVertical, MessageCircle, Globe, Target, Upload, Image as ImageIcon, Loader2, Copy, Code2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const FIELD_TYPES = ["text", "textarea", "number", "date", "phone", "select"];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const resolveImg = (url) => (!url ? "" : url.startsWith("http") ? url : `${BACKEND_URL}${url}`);

// Public form embed configuration
const EMBED_ORIGIN = "https://dashboard-utmataram.id";

const IFRAME_SNIPPET = `<!-- UT Mataram - Form Pendaftaran (embed) -->
<iframe
  id="utm-apply-frame"
  src="${EMBED_ORIGIN}/apply?embed=1"
  title="Pendaftaran Mahasiswa Baru UT Mataram"
  loading="lazy"
  scrolling="no"
  style="width:100%;border:0;min-height:640px;overflow:hidden"
></iframe>
<script>
  window.addEventListener("message", function (e) {
    if (e.origin !== "${EMBED_ORIGIN}") return;
    if (e.data && e.data.type === "utm-apply-height") {
      var f = document.getElementById("utm-apply-frame");
      if (f) f.style.height = e.data.height + "px";
    }
  });
</script>`;

const POPUP_SNIPPET = `<!-- UT Mataram - Tombol Pendaftaran (popup) -->
<button id="utm-apply-open"
  style="background:#007AFF;color:#fff;border:0;border-radius:9999px;padding:12px 24px;font-size:15px;font-weight:600;cursor:pointer;box-shadow:0 6px 20px rgba(0,122,255,.35)">
  Daftar Sekarang
</button>
<script>
(function () {
  var URL_ = "${EMBED_ORIGIN}/apply?embed=1";
  document.getElementById("utm-apply-open").addEventListener("click", function () {
    var o = document.createElement("div");
    o.style.cssText = "position:fixed;inset:0;background:rgba(0,0,0,.65);z-index:99999;display:flex;align-items:center;justify-content:center;padding:16px";
    var box = document.createElement("div");
    box.style.cssText = "position:relative;width:100%;max-width:960px;height:90vh;background:#0c0c0e;border-radius:16px;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,.5)";
    var c = document.createElement("button");
    c.innerHTML = "&times;";
    c.style.cssText = "position:absolute;top:10px;right:14px;z-index:2;background:rgba(255,255,255,.12);color:#fff;border:0;width:34px;height:34px;border-radius:50%;font-size:22px;line-height:1;cursor:pointer";
    c.onclick = function () { document.body.removeChild(o); };
    var f = document.createElement("iframe");
    f.src = URL_; f.style.cssText = "width:100%;height:100%;border:0";
    box.appendChild(c); box.appendChild(f); o.appendChild(box);
    o.addEventListener("click", function (e) { if (e.target === o) document.body.removeChild(o); });
    document.body.appendChild(o);
  });
})();
</script>`;

export default function SettingsPage() {
  const appSettings = useAppSettings();
  const [brand, setBrand] = useState({ app_name: "", app_subtitle: "", logo_url: "" });
  useEffect(() => {
    setBrand({
      app_name: appSettings.app_name || "",
      app_subtitle: appSettings.app_subtitle || "",
      logo_url: appSettings.logo_url || "",
    });
  }, [appSettings.app_name, appSettings.app_subtitle, appSettings.logo_url]);
  const saveBrand = async () => {
    try {
      await api.patch("/app-settings", brand);
      toast.success("Branding updated");
      appSettings.reload && appSettings.reload();
    } catch (e) {
      toast.error("Failed");
    }
  };

  // ----- Public Apply page hero (image + copy) -----
  const [apply, setApply] = useState({ apply_hero_image_url: "", apply_hero_title: "", apply_hero_subtitle: "" });
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    setApply({
      apply_hero_image_url: appSettings.apply_hero_image_url || "",
      apply_hero_title: appSettings.apply_hero_title || "",
      apply_hero_subtitle: appSettings.apply_hero_subtitle || "",
    });
  }, [appSettings.apply_hero_image_url, appSettings.apply_hero_title, appSettings.apply_hero_subtitle]);

  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/upload-image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setApply((p) => ({ ...p, apply_hero_image_url: data.url }));
      toast.success("Gambar terunggah. Klik Simpan untuk menerapkan.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengunggah gambar");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const saveApply = async () => {
    try {
      await api.patch("/app-settings", apply);
      toast.success("Halaman pendaftaran publik diperbarui");
      appSettings.reload && appSettings.reload();
    } catch (e) {
      toast.error("Gagal menyimpan");
    }
  };

  const useDefaultHero = async () => {
    const next = { ...apply, apply_hero_image_url: "" };
    setApply(next);
    try {
      await api.patch("/app-settings", next);
      toast.success("Tampilan default (gaya halaman login) diterapkan");
      appSettings.reload && appSettings.reload();
    } catch (e) {
      toast.error("Gagal menerapkan default");
    }
  };

  const copySnippet = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} disalin ke clipboard`),
      () => toast.error("Gagal menyalin"),
    );
  };

  // ----- Layanan & Keluhan (ticket) config -----
  const [ticketCfg, setTicketCfg] = useState({
    service_topics: "", complaint_topics: "", prodi_options: "",
    ticket_thankyou_layanan: "", ticket_thankyou_keluhan: "",
    ticket_phone_label: "", ticket_phone_enabled: true, ticket_phone_required: true,
  });
  useEffect(() => {
    setTicketCfg({
      service_topics: (appSettings.service_topics || []).join("\n"),
      complaint_topics: (appSettings.complaint_topics || []).join("\n"),
      prodi_options: (appSettings.prodi_options || []).join("\n"),
      ticket_thankyou_layanan: appSettings.ticket_thankyou_layanan || "",
      ticket_thankyou_keluhan: appSettings.ticket_thankyou_keluhan || "",
      ticket_phone_label: appSettings.ticket_phone_label || "No. Handphone",
      ticket_phone_enabled: appSettings.ticket_phone_enabled !== false,
      ticket_phone_required: appSettings.ticket_phone_required !== false,
    });
  }, [appSettings.service_topics, appSettings.complaint_topics, appSettings.prodi_options, appSettings.ticket_thankyou_layanan, appSettings.ticket_thankyou_keluhan, appSettings.ticket_phone_label, appSettings.ticket_phone_enabled, appSettings.ticket_phone_required]);

  const saveTicketCfg = async () => {
    const toList = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
    try {
      await api.patch("/app-settings", {
        service_topics: toList(ticketCfg.service_topics),
        complaint_topics: toList(ticketCfg.complaint_topics),
        prodi_options: toList(ticketCfg.prodi_options),
        ticket_thankyou_layanan: ticketCfg.ticket_thankyou_layanan,
        ticket_thankyou_keluhan: ticketCfg.ticket_thankyou_keluhan,
        ticket_phone_label: ticketCfg.ticket_phone_label || "No. Handphone",
        ticket_phone_enabled: ticketCfg.ticket_phone_enabled,
        ticket_phone_required: ticketCfg.ticket_phone_required,
      });
      toast.success("Konfigurasi Layanan & Keluhan disimpan");
      appSettings.reload && appSettings.reload();
    } catch (e) {
      toast.error("Gagal menyimpan");
    }
  };

  // ----- Ticket custom fields (Super Admin) -----
  const [customFields, setCustomFields] = useState([]);
  useEffect(() => {
    setCustomFields((appSettings.ticket_custom_fields || []).map((f) => ({
      key: f.key, label: f.label || "", type: f.type || "text",
      required: !!f.required, options: (f.options || []).join(", "),
    })));
  }, [appSettings.ticket_custom_fields]);

  const addCustomField = () => setCustomFields((p) => [...p, { label: "", type: "text", required: false, options: "" }]);
  const updateCustomField = (i, patch) => setCustomFields((p) => p.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const removeCustomField = (i) => setCustomFields((p) => p.filter((_, idx) => idx !== i));
  const saveCustomFields = async () => {
    const fields = customFields
      .map((f) => ({
        key: f.key || undefined,
        label: (f.label || "").trim(),
        type: f.type,
        required: !!f.required,
        options: f.type === "select" ? (f.options || "").split(",").map((x) => x.trim()).filter(Boolean) : [],
      }))
      .filter((f) => f.label);
    for (const f of fields) {
      if (f.type === "select" && f.options.length === 0) {
        toast.error(`Field pilihan "${f.label}" wajib punya minimal satu opsi`);
        return;
      }
    }
    try {
      await api.patch("/app-settings/ticket-fields", { fields });
      toast.success("Custom field disimpan");
      appSettings.reload && appSettings.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan custom field");
    }
  };


  const [stages, setStages] = useState([]);
  const [activeStage, setActiveStage] = useState(null);

  // ----- Hasil Akhir global config -----
  const [haOptions, setHaOptions] = useState([]);
  const [haSaving, setHaSaving] = useState(false);
  const loadHa = async () => {
    try {
      const { data } = await api.get("/app-settings/hasil-akhir");
      setHaOptions(data.options || []);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { loadHa(); }, []);
  const addHaOption = () => setHaOptions((p) => [...p, { name: "", bucket: "progress" }]);
  const updateHaOption = (idx, key, val) =>
    setHaOptions((p) => p.map((o, i) => (i === idx ? { ...o, [key]: val } : o)));
  const removeHaOption = (idx) => setHaOptions((p) => p.filter((_, i) => i !== idx));
  const saveHa = async () => {
    setHaSaving(true);
    try {
      const cleaned = haOptions
        .map((o) => ({ name: (o.name || "").trim(), bucket: o.bucket }))
        .filter((o) => o.name);
      if (cleaned.length === 0) {
        toast.error("Minimal satu opsi");
        return;
      }
      await api.patch("/app-settings/hasil-akhir", { options: cleaned });
      toast.success("Konfigurasi Hasil Akhir tersimpan & sinkron ke semua stage");
      load();
      loadHa();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setHaSaving(false);
    }
  };

  const load = async () => {
    const { data } = await api.get("/stages");
    setStages(data);
    if (data.length > 0 && !activeStage) setActiveStage(data[0].id);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const current = stages.find((s) => s.id === activeStage);

  const updateField = (idx, key, val) => {
    setStages((prev) =>
      prev.map((s) => {
        if (s.id !== activeStage) return s;
        const newFields = [...s.fields];
        newFields[idx] = { ...newFields[idx], [key]: val };
        return { ...s, fields: newFields };
      })
    );
  };

  const addField = () => {
    setStages((prev) =>
      prev.map((s) => (s.id === activeStage ? { ...s, fields: [...s.fields, { key: "", label: "", type: "text" }] } : s))
    );
  };

  const removeField = (idx) => {
    setStages((prev) =>
      prev.map((s) => (s.id === activeStage ? { ...s, fields: s.fields.filter((_, i) => i !== idx) } : s))
    );
  };

  // Drag and drop reorder for fields
  const [dragFieldIdx, setDragFieldIdx] = useState(null);
  const onFieldDragStart = (idx) => setDragFieldIdx(idx);
  const onFieldDragOver = (e) => e.preventDefault();
  const onFieldDrop = (idx) => {
    if (dragFieldIdx == null || dragFieldIdx === idx) return;
    setStages((prev) =>
      prev.map((s) => {
        if (s.id !== activeStage) return s;
        const list = [...s.fields];
        const [moved] = list.splice(dragFieldIdx, 1);
        list.splice(idx, 0, moved);
        return { ...s, fields: list };
      })
    );
    setDragFieldIdx(null);
  };

  // Drag and drop reorder for stages
  const [dragStageIdx, setDragStageIdx] = useState(null);
  const onStageDragStart = (idx) => setDragStageIdx(idx);
  const onStageDragOver = (e) => e.preventDefault();
  const onStageDrop = async (idx) => {
    if (dragStageIdx == null || dragStageIdx === idx) return;
    const newList = [...stages];
    const [moved] = newList.splice(dragStageIdx, 1);
    newList.splice(idx, 0, moved);
    setStages(newList);
    setDragStageIdx(null);
    try {
      await api.post("/stages/reorder", { ids: newList.map((s) => s.id) });
      toast.success("Urutan stage diperbarui");
      load();
    } catch (e) {
      toast.error("Gagal memperbarui urutan");
    }
  };

  const saveStage = async () => {
    if (!current) return;
    try {
      await api.patch(`/stages/${current.id}`, {
        name: current.name,
        description: current.description,
        fields: current.fields,
        wa_templates: current.wa_templates || [],
        public_form_enabled: !!current.public_form_enabled,
      });
      toast.success("Stage saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const deleteStage = async () => {
    if (!current) return;
    if (!confirm(`Delete stage "${current.name}"?`)) return;
    try {
      await api.delete(`/stages/${current.id}`);
      toast.success("Stage deleted");
      setActiveStage(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const createStage = async (data) => {
    try {
      const { data: newStage } = await api.post("/stages", data);
      toast.success("Stage created");
      load();
      setActiveStage(newStage.id);
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Schema</div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight mt-1">
            Stage & field configuration
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Customize pipeline stages and their data fields. Changes apply across the CRM immediately.
          </p>
        </div>
        <NewStageDialog onCreate={createStage} order={stages.length + 1} />
      </div>

      {/* App branding */}
      <Card className="bg-[#121212] border-border p-6 rounded-md">
        <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-1">Branding</div>
        <div className="font-heading text-lg text-white font-semibold mb-4">App name & logo</div>
        <div className="grid grid-cols-1 md:grid-cols-[120px_1fr_1fr_1fr_auto] gap-3 items-end">
          <div className="flex items-center justify-center">
            {brand.logo_url ? (
              <img src={brand.logo_url} alt="logo" className="w-20 h-20 rounded-md object-contain bg-white p-2" />
            ) : (
              <div className="w-20 h-20 rounded-md bg-primary flex items-center justify-center font-heading text-3xl text-white">
                {(brand.app_name || "C").slice(0, 1)}
              </div>
            )}
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">App name</label>
            <Input data-testid="brand-name-input" value={brand.app_name} onChange={(e) => setBrand({ ...brand, app_name: e.target.value })} className="bg-[#0A0A0A] border-border" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Subtitle</label>
            <Input value={brand.app_subtitle} onChange={(e) => setBrand({ ...brand, app_subtitle: e.target.value })} className="bg-[#0A0A0A] border-border" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Logo URL</label>
            <Input data-testid="brand-logo-input" value={brand.logo_url} onChange={(e) => setBrand({ ...brand, logo_url: e.target.value })} placeholder="https://..." className="bg-[#0A0A0A] border-border" />
          </div>
          <Button data-testid="save-brand-button" onClick={saveBrand} className="bg-primary hover:bg-[#0056B3]">
            <Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> Save
          </Button>
        </div>
      </Card>

      {/* Public Apply page hero */}
      <Card data-testid="apply-hero-card" className="bg-[#121212] border-border p-6 rounded-md">
        <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2 mb-1">
          <ImageIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
          Halaman Pendaftaran Publik (/apply)
        </div>
        <div className="font-heading text-lg text-white font-semibold mb-1">Foto & teks ajakan</div>
        <p className="text-sm text-muted-foreground mb-4 max-w-2xl">
          Foto mahasiswa dan teks ajakan ini tampil pada halaman publik <code className="text-primary font-mono">/apply</code>.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-5">
          {/* Image preview + upload */}
          <div>
            <div className="aspect-[4/3] rounded-lg overflow-hidden border border-border bg-[#0A0A0A] flex items-center justify-center">
              {apply.apply_hero_image_url ? (
                <img src={resolveImg(apply.apply_hero_image_url)} alt="hero" data-testid="apply-hero-preview" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center text-muted-foreground text-xs px-4">
                  <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-40" strokeWidth={1.5} />
                  Belum ada foto
                </div>
              )}
            </div>
            <label className="cursor-pointer mt-3 block">
              <input type="file" accept="image/*" onChange={handleHeroUpload} className="hidden" data-testid="apply-hero-upload-input" disabled={uploading} />
              <span className="inline-flex w-full items-center justify-center h-9 px-3 text-sm rounded-md border border-border bg-transparent hover:bg-[#1C1C1E] transition-colors">
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />}
                {uploading ? "Mengunggah..." : "Unggah foto"}
              </span>
            </label>
            <Button
              type="button"
              data-testid="apply-hero-default-button"
              onClick={useDefaultHero}
              variant="outline"
              size="sm"
              className="w-full mt-2 border-border bg-transparent hover:bg-[#1C1C1E]"
            >
              <ImageIcon className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
              Default (gaya login)
            </Button>
          </div>
          {/* Text fields */}
          <div className="space-y-3">
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Judul / Ajakan</label>
              <Input
                data-testid="apply-hero-title-input"
                value={apply.apply_hero_title}
                onChange={(e) => setApply({ ...apply, apply_hero_title: e.target.value })}
                placeholder="Ayo bergabung bersama UT Mataram"
                className="bg-[#0A0A0A] border-border"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Sub-teks</label>
              <textarea
                data-testid="apply-hero-subtitle-input"
                value={apply.apply_hero_subtitle}
                onChange={(e) => setApply({ ...apply, apply_hero_subtitle: e.target.value })}
                rows={2}
                placeholder="Perguruan Tinggi Negeri Pelopor Kuliah Online di NTB"
                className="w-full bg-[#0A0A0A] border border-border rounded-md p-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div className="flex justify-end pt-2">
              <Button data-testid="save-apply-hero-button" onClick={saveApply} className="bg-primary hover:bg-[#0056B3]">
                <Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> Simpan
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Embed code for external websites */}
      <Card data-testid="embed-code-card" className="bg-[#121212] border-border p-6 rounded-md">
        <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2 mb-1">
          <Code2 className="w-3.5 h-3.5" strokeWidth={1.5} />
          Kode Embed Form Pendaftaran
        </div>
        <div className="font-heading text-lg text-white font-semibold mb-1">Tempel di website Anda</div>
        <p className="text-sm text-muted-foreground mb-4 max-w-2xl">
          Salin salah satu kode di bawah dan tempel ke halaman website Anda. Form menunjuk ke{" "}
          <code className="text-primary font-mono">{EMBED_ORIGIN}/apply</code>.
        </p>

        <div className="space-y-5">
          {/* Iframe embed */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-sm text-white font-medium">1. Iframe tersemat (embed langsung)</div>
                <div className="text-xs text-muted-foreground">Form muncul menyatu di halaman, tinggi otomatis menyesuaikan.</div>
              </div>
              <Button
                data-testid="copy-iframe-snippet"
                onClick={() => copySnippet(IFRAME_SNIPPET, "Kode iframe")}
                size="sm"
                variant="outline"
                className="border-border bg-transparent hover:bg-[#1C1C1E] shrink-0"
              >
                <Copy className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Copy
              </Button>
            </div>
            <pre className="bg-[#0A0A0A] border border-border rounded-md p-3 text-xs text-muted-foreground overflow-x-auto max-h-52 whitespace-pre">{IFRAME_SNIPPET}</pre>
          </div>

          {/* Popup embed */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-sm text-white font-medium">2. Tombol Popup</div>
                <div className="text-xs text-muted-foreground">Tombol "Daftar Sekarang" membuka form dalam jendela popup.</div>
              </div>
              <Button
                data-testid="copy-popup-snippet"
                onClick={() => copySnippet(POPUP_SNIPPET, "Kode popup")}
                size="sm"
                variant="outline"
                className="border-border bg-transparent hover:bg-[#1C1C1E] shrink-0"
              >
                <Copy className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Copy
              </Button>
            </div>
            <pre className="bg-[#0A0A0A] border border-border rounded-md p-3 text-xs text-muted-foreground overflow-x-auto max-h-52 whitespace-pre">{POPUP_SNIPPET}</pre>
          </div>
        </div>
      </Card>

      {/* Layanan & Keluhan config */}
      <Card data-testid="ticket-config-card" className="bg-[#121212] border-border p-6 rounded-md">
        <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2 mb-1">
          <Code2 className="w-3.5 h-3.5" strokeWidth={1.5} />
          Layanan & Keluhan (Publik /layanandankeluhan)
        </div>
        <div className="font-heading text-lg text-white font-semibold mb-1">Konfigurasi topik & pesan</div>
        <p className="text-sm text-muted-foreground mb-4 max-w-2xl">Satu item per baris. Digunakan pada form publik Layanan & Keluhan.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Topik Layanan</label>
            <textarea data-testid="cfg-service-topics" value={ticketCfg.service_topics} onChange={(e) => setTicketCfg({ ...ticketCfg, service_topics: e.target.value })} rows={6}
              className="w-full bg-[#0A0A0A] border border-border rounded-md p-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Topik Keluhan</label>
            <textarea data-testid="cfg-complaint-topics" value={ticketCfg.complaint_topics} onChange={(e) => setTicketCfg({ ...ticketCfg, complaint_topics: e.target.value })} rows={6}
              className="w-full bg-[#0A0A0A] border border-border rounded-md p-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Program Studi</label>
            <textarea data-testid="cfg-prodi-options" value={ticketCfg.prodi_options} onChange={(e) => setTicketCfg({ ...ticketCfg, prodi_options: e.target.value })} rows={6}
              className="w-full bg-[#0A0A0A] border border-border rounded-md p-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Pesan Terima Kasih — Layanan</label>
            <textarea data-testid="cfg-thankyou-layanan" value={ticketCfg.ticket_thankyou_layanan} onChange={(e) => setTicketCfg({ ...ticketCfg, ticket_thankyou_layanan: e.target.value })} rows={2}
              className="w-full bg-[#0A0A0A] border border-border rounded-md p-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Pesan Terima Kasih — Keluhan</label>
            <textarea data-testid="cfg-thankyou-keluhan" value={ticketCfg.ticket_thankyou_keluhan} onChange={(e) => setTicketCfg({ ...ticketCfg, ticket_thankyou_keluhan: e.target.value })} rows={2}
              className="w-full bg-[#0A0A0A] border border-border rounded-md p-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-border">
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Label Field No. Handphone</label>
            <input data-testid="cfg-phone-label" value={ticketCfg.ticket_phone_label}
              onChange={(e) => setTicketCfg({ ...ticketCfg, ticket_phone_label: e.target.value })}
              placeholder="No. Handphone"
              className="w-full bg-[#0A0A0A] border border-border rounded-md p-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" data-testid="cfg-phone-enabled" checked={ticketCfg.ticket_phone_enabled}
                onChange={(e) => setTicketCfg({ ...ticketCfg, ticket_phone_enabled: e.target.checked })}
                className="w-4 h-4 accent-primary" />
              <span className="text-sm text-white">Tampilkan field</span>
            </label>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" data-testid="cfg-phone-required" checked={ticketCfg.ticket_phone_required}
                disabled={!ticketCfg.ticket_phone_enabled}
                onChange={(e) => setTicketCfg({ ...ticketCfg, ticket_phone_required: e.target.checked })}
                className="w-4 h-4 accent-primary disabled:opacity-40" />
              <span className={`text-sm ${ticketCfg.ticket_phone_enabled ? "text-white" : "text-muted-foreground"}`}>Wajib diisi</span>
            </label>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button data-testid="save-ticket-config" onClick={saveTicketCfg} className="bg-primary hover:bg-[#0056B3]">
            <Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> Simpan
          </Button>
        </div>
      </Card>

      {/* Custom Fields config */}
      <Card data-testid="ticket-custom-fields-card" className="bg-[#121212] border-border p-6 rounded-md">
        <div className="flex items-center justify-between mb-1">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2">
              <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> Custom Field
            </div>
            <div className="font-heading text-lg text-white font-semibold mt-1">Field tambahan form Layanan & Keluhan</div>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">Tambahkan field khusus yang akan tampil di form publik, dashboard staff, dan ikut ter-export ke Excel.</p>
          </div>
          <Button data-testid="add-custom-field" onClick={addCustomField} variant="outline" className="border-border bg-[#0A0A0A] text-white hover:bg-[#1C1C1E] shrink-0">
            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Tambah Field
          </Button>
        </div>

        <div className="space-y-3 mt-4">
          {customFields.length === 0 && (
            <div className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-md">Belum ada custom field.</div>
          )}
          {customFields.map((f, i) => (
            <div key={i} data-testid={`custom-field-row-${i}`} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end p-3 rounded-md bg-[#0A0A0A] border border-border">
              <div className="md:col-span-4">
                <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Label</label>
                <Input data-testid={`custom-field-label-${i}`} value={f.label} onChange={(e) => updateCustomField(i, { label: e.target.value })}
                  placeholder="mis. Semester" className="bg-[#121212] border-border h-9" />
              </div>
              <div className="md:col-span-3">
                <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Tipe</label>
                <Select value={f.type} onValueChange={(v) => updateCustomField(i, { type: v })}>
                  <SelectTrigger data-testid={`custom-field-type-${i}`} className="bg-[#121212] border-border h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Teks</SelectItem>
                    <SelectItem value="number">Angka</SelectItem>
                    <SelectItem value="date">Tanggal</SelectItem>
                    <SelectItem value="select">Pilihan (dropdown)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3">
                {f.type === "select" ? (
                  <>
                    <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Opsi (pisah koma)</label>
                    <Input data-testid={`custom-field-options-${i}`} value={f.options} onChange={(e) => updateCustomField(i, { options: e.target.value })}
                      placeholder="A, B, C" className="bg-[#121212] border-border h-9" />
                  </>
                ) : <div />}
              </div>
              <div className="md:col-span-1 flex items-center h-9">
                <label className="flex items-center gap-1.5 cursor-pointer select-none">
                  <input type="checkbox" data-testid={`custom-field-required-${i}`} checked={f.required}
                    onChange={(e) => updateCustomField(i, { required: e.target.checked })} className="w-4 h-4 accent-primary" />
                  <span className="text-xs text-white">Wajib</span>
                </label>
              </div>
              <div className="md:col-span-1 flex items-center justify-end h-9">
                <button data-testid={`custom-field-remove-${i}`} onClick={() => removeCustomField(i)}
                  className="text-muted-foreground hover:text-[#FF453A] transition-colors" title="Hapus field">
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end mt-4">
          <Button data-testid="save-custom-fields" onClick={saveCustomFields} className="bg-primary hover:bg-[#0056B3]">
            <Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> Simpan Custom Field
          </Button>
        </div>
      </Card>

      {/* Hasil Akhir global config */}
      <Card data-testid="hasil-akhir-config-card" className="bg-[#121212] border-border p-6 rounded-md">
        <div className="flex items-center justify-between mb-1">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2">
              <Target className="w-3.5 h-3.5" strokeWidth={1.5} />
              Hasil Akhir · Konfigurasi Global
            </div>
            <div className="font-heading text-lg text-white font-semibold mt-1">
              Opsi & mapping ke dashboard
            </div>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">
              Opsi di sini di-sinkronkan otomatis ke field <code className="text-primary font-mono">hasil_akhir</code> pada
              <span className="text-primary"> semua stage</span>. Setiap opsi dipetakan ke salah satu dari 3 bucket dashboard:
              <span className="text-[#32D74B] font-medium"> Selesai</span>,
              <span className="text-[#FF9F0A] font-medium"> Progress</span>, atau
              <span className="text-[#0A84FF] font-medium"> Belum disentuh</span>.
            </p>
          </div>
          <Button onClick={addHaOption} variant="outline" size="sm" className="border-border bg-transparent">
            <Plus className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
            Tambah opsi
          </Button>
        </div>
        <div className="space-y-2 mt-4">
          {haOptions.map((o, idx) => (
            <div key={idx} data-testid={`ha-option-row-${idx}`} className="grid grid-cols-[1fr_200px_auto] gap-2 items-center p-2 border border-border rounded-md bg-[#0A0A0A]">
              <Input
                data-testid={`ha-option-name-${idx}`}
                value={o.name}
                placeholder="Nama opsi (mis. Terproses)"
                onChange={(e) => updateHaOption(idx, "name", e.target.value)}
                className="bg-[#121212] border-border h-9"
              />
              <Select value={o.bucket} onValueChange={(v) => updateHaOption(idx, "bucket", v)}>
                <SelectTrigger data-testid={`ha-option-bucket-${idx}`} className="bg-[#121212] border-border h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="selesai">→ Selesai</SelectItem>
                  <SelectItem value="progress">→ Progress</SelectItem>
                  <SelectItem value="belum">→ Belum disentuh</SelectItem>
                </SelectContent>
              </Select>
              <button
                onClick={() => removeHaOption(idx)}
                className="p-1.5 rounded-md bg-destructive/10 hover:bg-destructive/20 text-destructive border border-destructive/20"
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </div>
          ))}
          {haOptions.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-4">Belum ada opsi. Klik &quot;Tambah opsi&quot;.</div>
          )}
        </div>
        <div className="flex justify-end mt-4 pt-3 border-t border-border">
          <Button data-testid="save-ha-config-button" onClick={saveHa} disabled={haSaving} className="bg-primary hover:bg-[#0056B3]">
            <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
            {haSaving ? "Menyimpan..." : "Simpan & sinkron ke semua stage"}
          </Button>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
        {/* Stage list */}
        <Card className="bg-[#121212] border-border p-3 rounded-md h-fit">
          <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground px-2 mb-3">Stages</div>
          <div className="space-y-1">
            {stages.map((s, sIdx) => (
              <button
                key={s.id}
                onClick={() => setActiveStage(s.id)}
                draggable
                onDragStart={() => onStageDragStart(sIdx)}
                onDragOver={onStageDragOver}
                onDrop={() => onStageDrop(sIdx)}
                data-testid={`stage-config-${s.id}`}
                className={`w-full text-left px-3 py-2.5 rounded-md text-sm flex items-center justify-between transition-colors cursor-grab active:cursor-grabbing ${
                  activeStage === s.id
                    ? "bg-[#1C1C1E] text-white border-l-2 border-primary"
                    : "text-muted-foreground hover:bg-[#1C1C1E] hover:text-white"
                }`}
              >
                <div className="flex items-center gap-2">
                  <GripVertical className="w-3.5 h-3.5 text-muted-foreground" />
                  <div>
                    <div className="font-medium">{s.name}</div>
                    <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{s.fields.length} fields</div>
                  </div>
                </div>
                <span className="font-mono text-xs text-muted-foreground">#{s.order}</span>
              </button>
            ))}
          </div>
        </Card>

        {/* Editor */}
        {current ? (
          <Card className="bg-[#121212] border-border p-6 rounded-md">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Stage name</label>
                <Input
                  value={current.name}
                  onChange={(e) =>
                    setStages((p) => p.map((s) => (s.id === current.id ? { ...s, name: e.target.value } : s)))
                  }
                  className="bg-[#0A0A0A] border-border"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Description</label>
                <Input
                  value={current.description || ""}
                  onChange={(e) =>
                    setStages((p) => p.map((s) => (s.id === current.id ? { ...s, description: e.target.value } : s)))
                  }
                  className="bg-[#0A0A0A] border-border"
                />
              </div>
            </div>

            <div className="border-t border-border pt-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Fields</div>
                <Button onClick={addField} variant="outline" size="sm" className="border-border bg-transparent">
                  <Plus className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                  Add field
                </Button>
              </div>

              <div className="space-y-2">
                {current.fields.map((f, idx) => (
                  <div
                    key={idx}
                    draggable
                    onDragStart={() => onFieldDragStart(idx)}
                    onDragOver={onFieldDragOver}
                    onDrop={() => onFieldDrop(idx)}
                    data-testid={`field-row-${idx}`}
                    className="grid grid-cols-[auto_1fr_1fr_140px_1fr_auto_auto_auto] gap-2 items-center p-2 border border-border rounded-md bg-[#0A0A0A]"
                  >
                    <span className="cursor-grab active:cursor-grabbing" title="Drag to reorder">
                      <GripVertical className="w-4 h-4 text-muted-foreground" />
                    </span>
                    <Input
                      value={f.key}
                      placeholder="key"
                      onChange={(e) => updateField(idx, "key", e.target.value)}
                      className="bg-[#121212] border-border font-mono text-xs h-8"
                    />
                    <Input
                      value={f.label}
                      placeholder="Label"
                      onChange={(e) => updateField(idx, "label", e.target.value)}
                      className="bg-[#121212] border-border h-8"
                    />
                    <Select value={f.type} onValueChange={(v) => updateField(idx, "type", v)}>
                      <SelectTrigger className="bg-[#121212] border-border h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FIELD_TYPES.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={(f.options || []).join(", ")}
                      placeholder="opt1, opt2"
                      onChange={(e) => updateField(idx, "options", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
                      className="bg-[#121212] border-border text-xs h-8"
                    />
                    <label className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground cursor-pointer" title="Show in Leads Pipeline table">
                      <input
                        type="checkbox"
                        checked={f.show_in_table !== false}
                        onChange={(e) => updateField(idx, "show_in_table", e.target.checked)}
                        className="accent-primary"
                      />
                      Table
                    </label>
                    <label className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground cursor-pointer" title="Show in public Apply form">
                      <input
                        type="checkbox"
                        checked={!!f.show_in_public}
                        onChange={(e) => updateField(idx, "show_in_public", e.target.checked)}
                        className="accent-primary"
                      />
                      Public
                    </label>
                    <button onClick={() => removeField(idx)} className="p-1.5 rounded-md bg-destructive/10 hover:bg-destructive/20 text-destructive border border-destructive/20">
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-between mt-6 pt-5 border-t border-border">
              <Button variant="outline" onClick={deleteStage} className="border-destructive/40 text-destructive bg-transparent hover:bg-destructive/10">
                <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Delete stage
              </Button>
              <Button data-testid="save-stage-button" onClick={saveStage} className="bg-primary hover:bg-[#0056B3]">
                <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Save changes
              </Button>
            </div>
          </Card>
        ) : (
          <Card className="bg-[#121212] border-border p-12 rounded-md text-center text-muted-foreground">
            Select a stage to edit, or create a new one.
          </Card>
        )}
      </div>

      {current && (
        <>
          {/* WhatsApp Templates */}
          <Card className="bg-[#121212] border-border p-6 rounded-md">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2">
                  <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
                  WhatsApp templates
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  Use placeholders like <code className="text-primary font-mono">{"{nama}"}</code>, <code className="text-primary font-mono">{"{nim}"}</code>, <code className="text-primary font-mono">{"{nominal_tagihan}"}</code>
                </div>
              </div>
              <Button
                onClick={() =>
                  setStages((p) =>
                    p.map((s) =>
                      s.id === current.id
                        ? { ...s, wa_templates: [...(s.wa_templates || []), { name: "", message: "" }] }
                        : s
                    )
                  )
                }
                variant="outline"
                size="sm"
                className="border-border bg-transparent"
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                Add template
              </Button>
            </div>
            <div className="space-y-2">
              {(current.wa_templates || []).map((t, idx) => (
                <div key={idx} className="grid grid-cols-[180px_1fr_auto] gap-2 items-start p-2 border border-border rounded-md bg-[#0A0A0A]">
                  <Input
                    value={t.name}
                    placeholder="Template name"
                    onChange={(e) => {
                      const v = e.target.value;
                      setStages((p) =>
                        p.map((s) => {
                          if (s.id !== current.id) return s;
                          const list = [...(s.wa_templates || [])];
                          list[idx] = { ...list[idx], name: v };
                          return { ...s, wa_templates: list };
                        })
                      );
                    }}
                    className="bg-[#121212] border-border h-9"
                  />
                  <textarea
                    value={t.message}
                    placeholder="Halo {nama}, ..."
                    onChange={(e) => {
                      const v = e.target.value;
                      setStages((p) =>
                        p.map((s) => {
                          if (s.id !== current.id) return s;
                          const list = [...(s.wa_templates || [])];
                          list[idx] = { ...list[idx], message: v };
                          return { ...s, wa_templates: list };
                        })
                      );
                    }}
                    rows={2}
                    className="bg-[#121212] border border-border rounded-md p-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <button
                    onClick={() =>
                      setStages((p) =>
                        p.map((s) =>
                          s.id === current.id
                            ? { ...s, wa_templates: (s.wa_templates || []).filter((_, i) => i !== idx) }
                            : s
                        )
                      )
                    }
                    className="p-1.5 rounded-md bg-destructive/10 hover:bg-destructive/20 text-destructive border border-destructive/20"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              ))}
              {(current.wa_templates || []).length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-4">No templates yet.</div>
              )}
            </div>
          </Card>

          {/* Public form toggle */}
          <Card className="bg-[#121212] border-border p-6 rounded-md">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2">
                  <Globe className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Public lead-capture form
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  When enabled, public visitors can submit leads at{" "}
                  <code className="text-primary font-mono">/apply</code> and they land in this stage.
                </div>
              </div>
              <Switch
                data-testid="public-form-toggle"
                checked={!!current.public_form_enabled}
                onCheckedChange={(v) =>
                  setStages((p) =>
                    p.map((s) => (s.id === current.id ? { ...s, public_form_enabled: v } : s))
                  )
                }
              />
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function NewStageDialog({ onCreate, order }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const submit = async () => {
    if (!name.trim()) return;
    await onCreate({ name, description, order, fields: [] });
    setName("");
    setDescription("");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-stage-button" className="bg-primary hover:bg-[#0056B3]">
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
          New stage
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#121212] border-border">
        <DialogHeader>
          <DialogTitle className="font-heading">New stage</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0A0A0A] border-border" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground block mb-1.5">Description</label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} className="bg-[#0A0A0A] border-border" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-border bg-transparent">Cancel</Button>
          <Button onClick={submit} className="bg-primary hover:bg-[#0056B3]">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
